import type { DocId, IsoDate, MemberRolesMap, Timestamp, UserId } from './common.js';

export type ExecutionStatus =
  | 'not_started'
  | 'in_progress'
  | 'blocked'
  | 'done'
  | 'skipped';

/**
 * Canonical Execution document ID.
 *
 * Executions use a *deterministic* ID formed as `{runId}_{taskId}` rather
 * than an auto-ID. This guarantees:
 *   - idempotent batch creation (re-running generation is a no-op)
 *   - direct O(1) lookup from (runId, taskId) without a query
 *   - uniqueness without a compound-unique-index workaround
 *
 * See design-addendum.md §1.
 */
export type ExecutionId = `${string}_${string}`;

/** Build a deterministic Execution ID. Use this in BOTH web and functions. */
export function makeExecutionId(runId: DocId, taskId: DocId): ExecutionId {
  return `${runId}_${taskId}`;
}

/**
 * `/projects/{projectId}/executions/{executionId}` - the (run × task)
 * intersection. `executionId === makeExecutionId(runId, taskId)`.
 */
export interface ExecutionDoc {
  executionId: ExecutionId;
  projectId: string;
  runId: DocId;
  taskId: DocId;

  /** Non-null: the `order` of the parent run at the time of creation.
   *  Used to cheaply query "previous run's execution for the same task"
   *  without fetching the run doc (see addendum §5). */
  runOrder: number;

  /** Denormalized task fields; refreshed by the task onUpdate trigger. */
  taskName: string;
  taskCategory: string;

  /**
   * Per-task, per-run planned dates (calendar days, `YYYY-MM-DD`). Distinct
   * from the run-wide {@link RunDoc.plannedStartDate} / `plannedEndDate`,
   * which apply to the whole phase: these hold the schedule for *this one
   * task* within *this one run* — the granularity the migration xlsx carries
   * in its per-task MT5 plan columns. Mirrors the planned/actual split on
   * {@link WbsItemDoc} (`plannedStart` / `plannedEnd`).
   *
   * Optional / `null` on executions without a per-task plan (everything
   * generated before this field existed, and any run whose tasks were not
   * imported from a dated source).
   */
  plannedStartDate?: IsoDate | null;
  plannedEndDate?: IsoDate | null;

  status: ExecutionStatus;
  actualAssigneeUserId: UserId | null;
  startedAt: Timestamp | null;
  completedAt: Timestamp | null;

  handoverNotes: string;
  /**
   * Markdown handoff aimed at the **successor tasks within the same run**
   * (= tasks whose `dependsOn` includes this task). Contrast with
   * `handoverNotes`, which is read by the same task's execution in the
   * NEXT phase. Auto-saved on every keystroke as a draft; only becomes
   * visible to downstream readers once "投稿" sets `successorNotesPostedAt`.
   */
  successorNotes: string;
  /**
   * Wall-clock time the author last hit "投稿" on `successorNotes`. `null`
   * while only a draft exists — readers use this as the "is published"
   * signal AND as the timestamp shown in the predecessor banner. Re-posting
   * overwrites with the new server time.
   */
  successorNotesPostedAt: Timestamp | null;
  /** UID of the user who last posted `successorNotes`. `null` until first post. */
  successorNotesPostedByUserId: UserId | null;
  backlogIssueUrls: string[];
  /** Extracted issue keys (e.g. "MIG-123") derived from backlogIssueUrls. */
  backlogIssueKeys: string[];

  memberUserIds: UserId[];
  memberRoles: MemberRolesMap;

  updatedAt: Timestamp;
  updatedByUserId: UserId;
}
